import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getRootUrl } from '@share-a-cart/shared-utils';
import dayjs from 'dayjs';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCopyToClipboard } from 'src/hooks';
import ImgUnavailable from 'src/images/other/unavailable.svg?react';
import type { AmazonItemDeal } from 'src/types';
import { formatPrice } from 'src/utils/formatPrice';
import { ItemThumbnail } from '../common/ItemThumbnail';
import {
  AMAZON_ITEM_DEALS_REGION,
  amazonItemPermalink,
  amazonItemPrice,
  amazonItemTitle,
  amazonPriceTimeFormat,
  shownAmazonBadge,
} from './amazonItemFilters';
import { AmazonPriceDisclaimer } from './AmazonPriceDisclaimer';
import { DealExpiryPill } from './DealExpiryPill';
import { AMAZON_STORE_LABEL, AMAZON_STORE_SLUG, dealsPath, startedWithinDay } from './dealFilters';
import { NewDealBadge } from './NewDealBadge';

type Props = {
  item: AmazonItemDeal;
  expanded?: boolean;
};

const chipClass = 'rounded px-1.5 py-0.5 text-xs font-semibold';
const AMAZON_LOGO_SRC = `${process.env.REACT_APP_IMG_URL}/images/favis/32/${AMAZON_STORE_SLUG}.png`;

export const AmazonItemDealCard = ({ item, expanded = false }: Props) => {
  const { t } = useTranslation();
  const { deal } = item;
  const title = amazonItemTitle(item);
  const ccy = deal.ccy ?? 'USD';
  const price = amazonItemPrice(item);
  const badge = shownAmazonBadge(deal.badge);
  const priceTime = dayjs(item.priceAsOf).format(amazonPriceTimeFormat(item.priceAsOf));
  const permalink = amazonItemPermalink(item);
  const copyToClipboard = useCopyToClipboard();
  const storePath = dealsPath(AMAZON_STORE_SLUG);
  const storeLinkClass = 'transition-opacity hover:opacity-80';

  const handleCopyLink = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'Deals',
        eventCategory: 'engagement',
        eventLabel: 'Deal Link Copied',
        eventValue: { vendor: AMAZON_STORE_SLUG, asin: item.asin },
      },
    });
    copyToClipboard(`${getRootUrl()}${permalink}`, { content: t('deals.link_copied') });
  };

  return (
    <div className='flex h-full min-w-0 flex-col gap-3 overflow-hidden rounded-xl bg-[#FBE9BA] p-4'>
      <div className='flex min-w-0 items-start gap-3'>
        <Link
          to={permalink}
          aria-hidden
          tabIndex={-1}
          className={`relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white ${storeLinkClass}`}
        >
          <ItemThumbnail
            className='h-full w-full object-contain p-0.5'
            src={item.image}
            alt={title}
            fallback={<ImgUnavailable className='h-9 w-9' />}
          />
          <img
            src={AMAZON_LOGO_SRC}
            alt=''
            aria-hidden
            className='absolute bottom-0.5 right-0.5 h-3.5 w-3.5 rounded-sm bg-white'
          />
        </Link>

        <div className='flex min-w-0 flex-1 flex-col gap-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <Link
              to={storePath}
              className={`font-poppins text-[11px] font-semibold uppercase tracking-widest text-[#8b6914] ${storeLinkClass}`}
            >
              {AMAZON_STORE_LABEL}
            </Link>
            {startedWithinDay(deal.startTime) && <NewDealBadge />}
          </div>
          <h3 className={`heading-200 !text-[#302404] ${expanded ? '' : 'line-clamp-2'}`}>
            <Link to={permalink} className={storeLinkClass}>
              {title}
            </Link>
          </h3>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-2'>
        {badge && (
          <span translate='no' className={`${chipClass} bg-white text-[#302404]`}>
            {badge}
          </span>
        )}
        {deal.state === 'prime' && (
          <span className={`${chipClass} bg-white text-[#302404]`}>
            {t('deals.amazon.prime_only')}
          </span>
        )}
        {deal.savingsPercent != null && (
          <span className={`${chipClass} bg-primary text-offWhite`}>
            {t('deals.amazon.percent_off', { pct: deal.savingsPercent })}
          </span>
        )}
      </div>

      <div className='mt-auto flex flex-col gap-1'>
        {price != null && (
          <div className='flex flex-wrap items-baseline gap-x-2 gap-y-0.5'>
            <p className='heading-300 !text-[#302404]'>
              {formatPrice(price, AMAZON_ITEM_DEALS_REGION, ccy, '$', false)}
            </p>
            <span className='flex items-center gap-1 text-xs text-[#8b6914]'>
              {t('deals.amazon.price_as_of_short', { time: priceTime })}
              <AmazonPriceDisclaimer priceTime={priceTime} />
            </span>
          </div>
        )}
        {deal.savingBasisLabel && deal.basisAmount != null && (
          <p className='text-100 text-[#3c2c04]'>
            <span translate='no'>{deal.savingBasisLabel}</span>{' '}
            <span className='line-through'>
              {formatPrice(deal.basisAmount, AMAZON_ITEM_DEALS_REGION, ccy, '$', false)}
            </span>
          </p>
        )}
        <DealExpiryPill expiresAt={deal.endTime} />
      </div>

      <div className='flex gap-2'>
        <a
          href={`${item.dealClickBase}/web`}
          target='_blank'
          rel='sponsored nofollow noopener noreferrer'
          className='button-primary flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 !px-3'
          onClick={() => {
            TagManager.dataLayer({
              dataLayer: {
                event: 'Deals',
                eventCategory: 'engagement',
                eventLabel: 'Deal Clicked',
                eventValue: { vendor: AMAZON_STORE_SLUG, asin: item.asin },
              },
            });
          }}
        >
          {t('deals.amazon.view')}
          <ArrowForwardIcon style={{ fontSize: '16px' }} />
        </a>
        <button
          type='button'
          onClick={handleCopyLink}
          aria-label={t('common.copy_link')}
          className='button-secondary flex h-10 w-10 shrink-0 items-center justify-center !px-0 hover:!bg-[#f6d06d]'
        >
          <ContentCopyIcon style={{ fontSize: '18px' }} />
        </button>
      </div>
    </div>
  );
};
